import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flame, Hand, Heart, Star, Dumbbell } from "lucide-react";
import { toast } from "sonner";

interface ParticipantReactionsProps {
  challengeId: string;
  participantId: string;
  userId: string;
}

const REACTION_ICONS = {
  fire: { icon: Flame, color: "text-accent", label: "Fire" },
  clap: { icon: Hand, color: "text-primary", label: "Clap" },
  heart: { icon: Heart, color: "text-secondary", label: "Heart" },
  star: { icon: Star, color: "text-accent", label: "Star" },
  muscle: { icon: Dumbbell, color: "text-primary", label: "Muscle" },
};

export function ParticipantReactions({ challengeId, participantId, userId }: ParticipantReactionsProps) {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: reactions } = useQuery({
    queryKey: ['participant-reactions', participantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_reactions')
        .select('*')
        .eq('participant_id', participantId);
      
      if (error) throw error;
      return data;
    },
  });

  const toggleReactionMutation = useMutation({
    mutationFn: async ({ reactionType, shouldAdd }: { reactionType: string; shouldAdd: boolean }) => {
      if (!user) throw new Error("Not authenticated");

      if (shouldAdd) {
        // Add reaction
        const { error } = await supabase
          .from('challenge_reactions')
          .insert({
            challenge_id: challengeId,
            participant_id: participantId,
            user_id: user.id,
            reaction_type: reactionType,
          });

        if (error) throw error;

        // Create activity entry
        await supabase
          .from('challenge_activity')
          .insert({
            challenge_id: challengeId,
            user_id: user.id,
            activity_type: 'reaction',
            activity_data: { 
              reaction_type: reactionType,
              target_user: userId 
            }
          });
      } else {
        // Remove reaction
        const { error } = await supabase
          .from('challenge_reactions')
          .delete()
          .eq('participant_id', participantId)
          .eq('user_id', user.id)
          .eq('reaction_type', reactionType);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participant-reactions', participantId] });
      queryClient.invalidateQueries({ queryKey: ['challenge-activity', challengeId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update reaction");
    },
  });

  const myReactions = reactions?.filter(r => r.user_id === user?.id).map(r => r.reaction_type) || [];
  const reactionCounts = reactions?.reduce((acc, r) => {
    acc[r.reaction_type] = (acc[r.reaction_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(REACTION_ICONS).map(([type, { icon: Icon, color, label }]) => {
        const count = reactionCounts[type] || 0;
        const isActive = myReactions.includes(type);

        return (
          <Button
            key={type}
            variant={isActive ? "default" : "outline"}
            size="sm"
            onClick={() => toggleReactionMutation.mutate({ 
              reactionType: type, 
              shouldAdd: !isActive 
            })}
            disabled={toggleReactionMutation.isPending}
            className={`gap-1 ${isActive ? '' : 'hover:bg-muted'}`}
          >
            <Icon className={`h-4 w-4 ${color}`} />
            {count > 0 && <Badge variant="secondary" className="h-5 px-1">{count}</Badge>}
          </Button>
        );
      })}
    </div>
  );
}
